import type { ReactNode } from "react";
import { Alert02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import type { SenderRecommendation } from "../hooks/useRecommendedSender";

interface SenderMismatchDialogProps {
  opened: boolean;
  channel: "sms" | "email";
  recommendation: SenderRecommendation;
  currentLabel: ReactNode;
  recommendedLabel: ReactNode;
  onKeep: () => void;
  onSwitch: () => void;
  zIndex?: number;
}

const NOUN = { sms: "a phone number", email: "an email address" } as const;
const KIND = { sms: "number", email: "address" } as const;

const ComparisonRow = ({ term, value }: { term: string; value: ReactNode }) => (
  <span className="flex items-center justify-between gap-3 px-3 py-2.5">
    <span className="text-xs font-medium text-content-400">{term}</span>
    <span className="min-w-0 truncate text-sm font-bold text-content-1000">
      {value}
    </span>
  </span>
);

export const SenderMismatchDialog = ({
  opened,
  channel,
  recommendation,
  currentLabel,
  recommendedLabel,
  onKeep,
  onSwitch,
  zIndex
}: SenderMismatchDialogProps) => {
  const property = recommendation.propertyName ?? "this property";
  const copy = {
    last_used: {
      title: `Not the last ${KIND[channel]} used`,
      text: `You're about to respond from ${NOUN[channel]} that wasn't the last one used with this contact.`
    },
    property_default: {
      title: `Not the property's default ${KIND[channel]}`,
      text: `You're about to send from ${NOUN[channel]} that isn't the default for ${property}.`
    },
    property_assigned: {
      title: `Not the property's ${KIND[channel]}`,
      text: `You're about to send from ${NOUN[channel]} that isn't the one assigned to ${property}.`
    }
  }[recommendation.kind];

  return (
    <ConfirmDialog
      opened={opened}
      onClose={onKeep}
      onConfirm={onSwitch}
      variant="warning"
      icon={{ icon: Alert02Icon, variant: "warning" }}
      title={copy.title}
      description={
        <>
          {copy.text}
          <span className="mt-4 block divide-y divide-alpha-80 rounded-lg border border-alpha-80 text-left">
            <ComparisonRow term="Current" value={currentLabel} />
            <ComparisonRow term="Recommended" value={recommendedLabel} />
          </span>
        </>
      }
      cancelButton={{ label: "Keep current" }}
      confirmButton={{ label: "Switch to recommended" }}
      widthClassName="max-w-[540px]"
      zIndex={zIndex}
    />
  );
};
